"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import type { SubscriptionInfo, SubscriptionLimits } from "@/lib/stripe/types";
import { clientLogger } from "@/lib/client-logger";

interface TenantColors {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

interface UsageStatusProps {
  limits: SubscriptionLimits | undefined;
  subscription: SubscriptionInfo | null | undefined;
  currentCampaignCount: number;
  clueCount: number;
  onUpgrade: () => void;
}

export function UsageStatus({
  limits,
  subscription,
  currentCampaignCount,
  clueCount,
  onUpgrade,
}: UsageStatusProps) {
  const [tenantColors, setTenantColors] = useState<TenantColors | null>(null);

  useEffect(() => {
    const fetchTenantColors = async () => {
      try {
        const response = await fetch("/api/tenant/colors");
        if (response.ok) {
          const data = await response.json();
          if (data.colors) {
            setTenantColors(data.colors);
          }
        }
      } catch (error) {
        clientLogger.error("Error fetching tenant colors", {
          error: error instanceof Error ? error.message : "Unknown error"
        });
      }
    };
    fetchTenantColors();
  }, []);

  const isWhitelabel = tenantColors !== null;

  if (!limits) return null;

  return (
    <>
      {/* Subscription Status & Usage Warning */}
      <div
        className={`mb-6 p-4 rounded-lg ${!isWhitelabel ? 'bg-blue-50 border border-blue-200' : ''}`}
        style={isWhitelabel && tenantColors ? {
          backgroundColor: tenantColors.primaryColor,
        } : undefined}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`font-medium ${isWhitelabel ? 'text-white' : 'text-blue-800'}`}>Current Usage</h3>
            <div className={`text-sm mt-1 space-x-4 ${isWhitelabel ? 'text-white/80' : 'text-blue-600'}`}>
              <span>
                Active Campaigns: {currentCampaignCount}
                {limits.activeCampaigns ? ` / ${limits.activeCampaigns}` : ' (Unlimited)'}
              </span>
              <span>
                Locations per Campaign: {clueCount} (Unlimited)
              </span>
              <span>Plan: {subscription?.plan || 'FREE'}</span>
            </div>
          </div>
          {subscription?.plan === 'FREE' && (
            <Button
              onClick={onUpgrade}
              size="sm"
              style={isWhitelabel && tenantColors ? {
                backgroundColor: tenantColors.secondaryColor,
                borderColor: tenantColors.secondaryColor,
                color: "white",
              } : undefined}
              className={!isWhitelabel ? "bg-blue-600 hover:bg-blue-700" : ""}
            >
              Upgrade Plan
            </Button>
          )}
        </div>
      </div>

      {/* Approaching Limits Warning */}
      {limits?.activeCampaigns && currentCampaignCount >= Math.floor(limits.activeCampaigns * 0.8) && (
        <div
          className={`mb-6 p-4 rounded-lg ${!isWhitelabel ? 'bg-yellow-50 border border-yellow-200' : ''}`}
          style={isWhitelabel && tenantColors ? {
            backgroundColor: tenantColors.primaryColor,
          } : undefined}
        >
          <div className="flex items-center">
            <svg className={`w-5 h-5 mr-2 ${isWhitelabel ? 'text-white/60' : 'text-yellow-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h4 className={`font-medium ${isWhitelabel ? 'text-white' : 'text-yellow-800'}`}>Approaching Campaign Limit</h4>
              <p className={`text-sm mt-1 ${isWhitelabel ? 'text-white/80' : 'text-yellow-700'}`}>
                You&apos;re using {currentCampaignCount} of {limits.activeCampaigns} active campaigns. Consider upgrading before creating more.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
